import { useState, useRef, useEffect } from "react"
import { Check, ChevronsUpDown, Plus, Dices, Trash2, Loader2, Search } from "lucide-react" // Added Search
import { Button } from "@/components/ui/button"
import hive, { type TickerLookupResult, type Company } from "@/lib/api/hive"
import { type Stock } from "@/types/stock"
import { ExternalLookupDialog } from "./external-lookup-dialog"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"

interface AddPositionCardProps {
	onAddPosition: (stock: Stock, shares: number) => void
	onRandomize?: () => void
	onReset?: () => void // New Prop
}

export function AddPositionCard({ onAddPosition, onRandomize, onReset }: AddPositionCardProps) {
	const [open, setOpen] = useState(false)
	const [selectedStock, setSelectedStock] = useState<Stock | null>(null)
	const [shares, setShares] = useState("")
	const [searchQuery, setSearchQuery] = useState("")
	const [searchResults, setSearchResults] = useState<Stock[]>([])
	const [isLoading, setIsLoading] = useState(false)
	const [externalLookupOpen, setExternalLookupOpen] = useState(false)

	const sharesInputRef = useRef<HTMLInputElement>(null)

	const handleTickerAdded = (ticker: TickerLookupResult, company?: Company) => {
		const stock: Stock = company
			? {
					ticker: company.symbol,
					name: company.company_name,
					exchange: company.exchange,
					price: company.stock.price || 0,
					marketCap: (company.stock.market_cap || 0).toString(),
					sector: company.sector || "Unknown",
					dividendPerShare: company.stock.last_div || 0,
					dividendYield:
						(company.stock.price || 0) > 0
							? ((company.stock.last_div || 0) / (company.stock.price || 1)) * 100
							: 0,
					dividendGrowth5y: 0,
					frequency: "Quarterly" as const,
					currency: company.currency,
				}
			: {
					ticker: ticker.symbol,
					name: ticker.name,
					exchange: ticker.exchange,
					price: 0,
					marketCap: "0",
					sector: "Unknown",
					dividendPerShare: 0,
					dividendYield: 0,
					dividendGrowth5y: 0,
					frequency: "Quarterly" as const,
				}
		setSelectedStock(stock)
		setOpen(false)
		setTimeout(() => sharesInputRef.current?.focus(), 0)
	}

	useEffect(() => {
		const search = async () => {
			if (searchQuery.length < 2) {
				setSearchResults([])
				return
			}
			setIsLoading(true)
			try {
				const results = await hive.searchCompanies(searchQuery)
				// Fetch details for top 10 results to ensure accurate dividend data in the list using bulk API
				const bulkItems = results.slice(0, 10).map((r) => ({ symbol: r.symbol, exchange: r.exchange }))
				const detailedResults = await hive.getBulkStocks(bulkItems)

				setSearchResults(detailedResults)
			} catch (error) {
				console.error("Search failed", error)
			} finally {
				setIsLoading(false)
			}
		}

		const debounceTimer = setTimeout(search, 300)
		return () => clearTimeout(debounceTimer)
	}, [searchQuery])

	const handleAdd = () => {
		if (selectedStock && shares) {
			onAddPosition(selectedStock, parseFloat(shares))
			setSelectedStock(null)
			setShares("")
			return true
		}
		return false
	}

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			if (handleAdd()) {
				// Re-open the stock selector for quick chain-adding
				setTimeout(() => setOpen(true), 0)
			}
		}
	}

	return (
		<Card className="h-fit bg-white/60 dark:bg-black/20 backdrop-blur-md border-sky-900/10 dark:border-sky-100/10">
			<CardHeader className="flex flex-row items-start justify-between space-y-0 pb-4">
				<div className="space-y-1.5">
					<CardTitle className="text-sky-950 dark:text-sky-50">Add Position</CardTitle>
					<CardDescription>Search for a company to add to your hive.</CardDescription>
				</div>

				{/* ACTIONS (Randomize & Reset) */}
				<div className="flex items-center gap-1 -mt-1 -mr-2">
					{onRandomize && (
						<Button
							variant="ghost"
							size="icon"
							onClick={onRandomize}
							className="text-sky-900/40 hover:text-sky-600 hover:bg-sky-100/50"
							title="Generate Random Portfolio"
						>
							<Dices className="h-5 w-5" />
						</Button>
					)}

					{onReset && (
						<Button
							variant="ghost"
							size="icon"
							onClick={onReset}
							className="text-sky-900/40 hover:text-red-600 hover:bg-red-100/50"
							title="Reset Portfolio"
						>
							<Trash2 className="h-5 w-5" />
						</Button>
					)}
				</div>
			</CardHeader>

			<CardContent>
				{/* ROW LAYOUT */}
				<div className="flex flex-col md:flex-row gap-4 items-end">
					{/* STOCK SEARCH */}
					<div className="flex-1 space-y-1.5 w-full">
						<Label>Company</Label>
						<Popover open={open} onOpenChange={setOpen}>
							<PopoverTrigger asChild>
								<Button
									variant="outline"
									role="combobox"
									aria-expanded={open}
									className="justify-between w-full font-normal"
								>
									{selectedStock ? selectedStock.name : "Select stock..."}
									<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
								</Button>
							</PopoverTrigger>
							<PopoverContent className="w-[300px] p-0" align="start">
								<Command shouldFilter={false}>
									<CommandInput
										placeholder="Search company..."
										value={searchQuery}
										onValueChange={setSearchQuery}
									/>
									<CommandList>
										{isLoading && (
											<div className="flex items-center justify-center p-4">
												<Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
											</div>
										)}
										<CommandEmpty>
											{searchQuery.length < 2 ? "Type to search..." : "No stock found."}
										</CommandEmpty>
										<CommandGroup>
											{searchResults.map((stock) => (
												<CommandItem
													key={`${stock.ticker}-${stock.exchange}`}
													value={stock.name}
													onPointerDown={(e) => e.preventDefault()}
													onSelect={() => {
														setSelectedStock(stock)
														setOpen(false)
														// Preserve Quick Navigation: Focus shares input immediately
														setTimeout(() => sharesInputRef.current?.focus(), 0)
													}}
												>
													<Check
														className={cn(
															"mr-2 h-4 w-4",
															selectedStock?.ticker === stock.ticker
																? "opacity-100"
																: "opacity-0",
														)}
													/>
													<div className="flex flex-col">
														<span>{stock.name}</span>
														<span className="text-xs text-muted-foreground">
															{stock.ticker} • Yield: {stock.dividendYield.toFixed(2)}%
														</span>
													</div>
												</CommandItem>
											))}
										</CommandGroup>

										{searchQuery.length >= 2 && (
											<CommandGroup>
												<CommandItem
													onPointerDown={(e) => e.preventDefault()}
													onSelect={() => {
														setExternalLookupOpen(true)
														setOpen(false)
													}}
													className="text-sky-600 font-medium cursor-pointer"
												>
													<Search className="mr-2 h-4 w-4" />
													Can't find my company
												</CommandItem>
											</CommandGroup>
										)}
									</CommandList>
								</Command>
							</PopoverContent>
						</Popover>
					</div>

					{/* SHARES INPUT */}
					<div className="w-full md:w-32 space-y-1.5">
						<Label htmlFor="shares">Shares</Label>
						<Input
							ref={sharesInputRef}
							id="shares"
							type="number"
							placeholder="0"
							value={shares}
							onChange={(e) => setShares(e.target.value)}
							onKeyDown={handleKeyDown}
						/>
					</div>

					{/* ADD BUTTON */}
					<Button
						className="w-full md:w-auto bg-sky-600 hover:bg-sky-700 text-white"
						onClick={() => handleAdd()}
						disabled={!selectedStock || !shares}
					>
						<Plus className="h-4 w-4" />
						<span className="md:hidden ml-2">Add</span>
					</Button>
				</div>

				<ExternalLookupDialog
					open={externalLookupOpen}
					onOpenChange={setExternalLookupOpen}
					onTickerAdded={handleTickerAdded}
					initialQuery={searchQuery}
				/>
			</CardContent>
		</Card>
	)
}
